<template>
  <div class="flex gap-5 items-center">
    <div v-if="heading" :class="headingStyle">{{ heading }}</div>
    <div v-if="mode === 'toggle'" class="panel-body flex gap-2 items-center">
      <p v-if="!isStatusHide" class="text-primary-purple-600">{{ isChecked }}</p>
      <label class="switch relative inline-block w-11 h-6">
        <input v-model="isChecked" :disabled="disabled" type="checkbox" @click="toggleCheckbox" />
        <div
          class="slider round absolute left-0 right-0 top-0 bottom-0 cursor-pointer"
          :class="[{ 'slider-disable-bg': !isChecked }, checkboxColorClass]"
          :style="customStyle"
        ></div>
      </label>
    </div>

    <div
      v-else
      class="segmented-container"
      :style="{ '--segments': String(optionCount), '--active-index': String(activeIndex) }"
    >
      <div class="segmented-thumb"></div>
      <button
        v-for="(label, idx) in options"
        :key="idx"
        class="segmented-option"
        :disabled="disabled"
        :class="{ 'segmented-active': activeIndex === idx }"
        @click="selectOption(idx)"
      >
        {{ label }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    mode: {
      type: String,
      default: 'toggle',
      validator: (v) => ['toggle', 'switch'].includes(v),
    },
    headingStyle: {
      type: String,
      default: 'text-base leading-6 font-normal',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    heading: {
      type: String,
      default: '',
    },
    isStatusHide: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: [Boolean, String],
      default: false,
    },
    checkboxColorClass: {
      type: String,
      default: 'bg-primary-purple-600',
    },
    customStyle: {
      type: Object,
      default: () => ({}),
    },
    switchOptions: {
      type: Array,
      default: () => [],
      validator: (arr) => Array.isArray(arr),
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isChecked: false,
      selectedOption: null,
    }
  },
  computed: {
    options() {
      return this.getResolvedOptions()
    },
    optionCount() {
      return Math.max(this.options.length, 2)
    },
    activeIndex() {
      if (this.mode !== 'switch') return 0
      const index = this.options.findIndex((opt) => String(opt) === String(this.selectedOption))
      return index >= 0 ? index : 0
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        if (this.mode === 'toggle') {
          this.isChecked = !!value
        } else {
          const opts = this.getResolvedOptions()
          const hasMatch = opts.includes(value)
          this.selectedOption = hasMatch ? value : opts[0] || null
        }
      },
    },
    mode: {
      immediate: true,
      handler() {
        if (this.mode === 'switch' && !this.modelValue) {
          const [first] = this.getResolvedOptions()
          if (first) {
            this.selectedOption = first
            this.$emit('update:modelValue', first)
          }
        }
      },
    },
  },
  methods: {
    toggleCheckbox() {
      this.isChecked = !this.isChecked
      this.$emit('update:modelValue', this.isChecked)
    },
    selectOption(index) {
      const options = this.getResolvedOptions()
      const boundedIndex = Math.max(0, Math.min(index, options.length - 1))
      const next = options[boundedIndex]
      this.selectedOption = next
      this.$emit('update:modelValue', next)
    },
    getResolvedOptions() {
      if (Array.isArray(this.switchOptions) && this.switchOptions.length > 0) {
        return this.switchOptions.filter((v) => typeof v === 'string' && v.trim().length > 0)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.switch input {
  display: none;
}
.slider {
  -webkit-transition: 0.3s;
  transition: 0.3s;
}

.slider::before {
  position: absolute;
  bottom: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  content: '';
  background-color: white;
  -webkit-transition: 0.3s;
  transition: 0.3s;
}

input:focus + .slider {
  box-shadow: 0 0 0 4px var(--primary-purple-100);
}

input:checked + .slider::before {
  -webkit-transform: translateX(20px);
  -ms-transform: translateX(20px);
  transform: translateX(20px);
}

.slider.round {
  border-radius: 12px;
}

.slider.round::before {
  border-radius: 50%;
}
</style>

<style lang="scss">
.slider-disable-bg {
  background-color: var(--primary-gray-200);
}

/* Segmented switch styles */
.segmented-container {
  position: relative;
  display: grid;
  grid-template-columns: repeat(var(--segments, 2), 1fr);
  align-items: center;
  min-width: 180px;
  height: 30px;
  padding: 2px;
  overflow: hidden;
  background-color: var(--primary-purple-600);
  border-radius: 14px;
  gap: 0;
}

.segmented-thumb {
  position: absolute;
  top: 2px;
  bottom: 2px;
  left: calc(2px + (100% / var(--segments, 2)) * var(--active-index, 0));
  z-index: 0;
  width: calc((100% / var(--segments, 2)) - 4px);
  background-color: #fff;
  border-radius: 9999px;
  transition: left 0.25s ease;
}

.segmented-option {
  position: relative;
  z-index: 2;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0 8px;
  overflow: hidden;
  font-size: 12px;
  font-weight: 600;
  line-height: 26px;
  color: #fff;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: none;
}

.segmented-option.segmented-active {
  color: var(--primary-purple-600);
}
</style>
